import {
  faAlignJustify,
  faCalendar,
  faCheck,
  faEllipsisH,
  faGripVertical,
  faUserPlus
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AvatarTask from 'components/common/Avatar';
import Flex from 'components/common/Flex';
import SingleSelect from 'components/common/SingleSelect';
import DialogModal from 'components/modals/DialogModal';
import TemplateDaysToCompleteEditModal from 'components/modals/TemplateDaysToCompleteEditModal';
import TemplateTaskEditModal from 'components/modals/TemplateTaskEditModal';
import useApi from 'helpers/api';
import useNotification from 'hooks/useNotification';
import { useEffect, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import {
  Button,
  Card,
  Dropdown,
  Form,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import AgentPhotoService from 'services/AgentPhoto.service';

const SimpleTaskList = ({
  tasks = [],
  onTaskUpdate,
  onTaskDelete,
  onTaskReorder,
  isDisabled = false,
  showBulkActions = false,
  onBulkAction = null
}) => {
  const [localTaskStatus, setLocalTaskStatus] = useState({});
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [taskItems, setTaskItems] = useState([]);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [taskIdToDelete, setTaskIdToDelete] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showEditDaysToCompleteModal, setShowEditDaysToCompleteModal] =
    useState(false);
  const [taskToEdit, setTaskToEdit] = useState(null);
  const [taskToDaysToCompleteEdit, setTaskToDaysToCompleteEdit] =
    useState(null);
  const [agents, setAgents] = useState([]);
  const [formattedAgents, setFormattedAgents] = useState([]);

  const [editingField, setEditingField] = useState({
    taskId: null,
    field: null // 'title', 'days_to_complete', 'agent'
  });
  const [editData, setEditData] = useState({});
  const [selectedAgent, setSelectedAgent] = useState(null);

  const [bulkBody, setBulkBody] = useState({
    operation: '',
    days_to_complete: null,
    agentId: null
  });
  const [selectedBulkAction, setSelectedBulkAction] = useState(null);
  const [selectedAgentBulkAction, setSelectedAgentBulkAction] = useState(null);

  // Refs
  const selectAllCheckboxRef = useRef(null);
  const titleInputRef = useRef(null);
  const daysToCompleteInputRef = useRef(null);
  const agentSelectRef = useRef(null);
  const editingCardRef = useRef(null);
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(true);
  const [agentPhotos, setAgentPhotos] = useState({});

  const optionsBulkActions = [
    { value: '', label: 'Ações em massa' },
    { value: 'COMPLETE', label: 'Marcar como concluído' },
    { value: 'UNCOMPLETE', label: 'Marcar como não concluído' },
    { value: 'SET_DEADLINE', label: 'Definir prazo' },
    { value: 'SET_AGENT', label: 'Definir responsável' },
    { value: 'DELETE', label: 'Excluir' }
  ];

  // Efeitos
  useEffect(() => {
    if (tasks) {
      const statusMap = {};
      tasks.forEach(task => {
        statusMap[task.id] = task.status;
      });
      setLocalTaskStatus(statusMap);
      setTaskItems(tasks);
    }
  }, [tasks]);

  // Sincroniza o localTaskStatus quando as tasks mudam (ex: via bulk actions)
  useEffect(() => {
    if (tasks) {
      setLocalTaskStatus(prev => {
        const newStatusMap = { ...prev };
        tasks.forEach(task => {
          newStatusMap[task.id] = task.status;
        });
        return newStatusMap;
      });
    }
  }, [tasks]);

  useEffect(() => {
    // Formata os agentes para o SingleSelect
    const optionsAgents = agents.map(item => ({
      value: item.id,
      label: `${item.firstName} ${item.lastName}`
    }));

    optionsAgents.unshift({ value: null, label: 'Sem responsável' });
    setFormattedAgents(optionsAgents);
  }, [agents]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        editingField.taskId &&
        editingCardRef.current &&
        !editingCardRef.current.contains(event.target)
      ) {
        saveChanges(editingField.taskId);
      }
    };

    if (editingField.taskId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingField]);

  useEffect(() => {
    if (editingField.field === 'title' && titleInputRef.current) {
      titleInputRef.current.focus();
    } else if (
      editingField.field === 'days_to_complete' &&
      daysToCompleteInputRef.current
    ) {
      daysToCompleteInputRef.current.focus();
      daysToCompleteInputRef.current.click();
    } else if (editingField.field === 'agent' && agentSelectRef.current) {
      agentSelectRef.current.focus();
    }
  }, [editingField]);

  useEffect(() => {
    if (selectAllCheckboxRef.current && tasks?.length > 0) {
      const isIndeterminate =
        selectedTasks.length > 0 && selectedTasks.length < tasks.length;
      const isChecked =
        tasks.length > 0 && selectedTasks.length === tasks.length;

      selectAllCheckboxRef.current.indeterminate = isIndeterminate;
      selectAllCheckboxRef.current.checked = isChecked;
    }
  }, [selectedTasks, tasks]);

  useEffect(() => {
    // Só executa na montagem inicial ou quando agents ainda não foram carregados
    if (agents.length === 0) {
      getAgents();
    }
    fetchAgentPhotos();
  }, []);

  // Função para determinar cor da quantidade de dias
  const getDaysToCompleteColor = daysToComplete => {
    if (daysToComplete === undefined || daysToComplete === null)
      return 'text-secondary';
    if (daysToComplete <= 0) return 'text-primary';
    if (daysToComplete <= 3) return 'text-warning';
    return 'text-info';
  };

  // Funções para abrir modais
  const openEditModal = task => {
    setTaskToEdit(task);
    setShowEditModal(true);
  };

  const openDaysToCompleteModal = task => {
    setTaskToDaysToCompleteEdit(task);
    setShowEditDaysToCompleteModal(true);
  };

  // Funções de edição
  const startEditing = (task, field) => {
    if (editingField.taskId !== null) {
      saveChanges(editingField.taskId);
    }

    setEditingField({
      taskId: task.id,
      field: field
    });

    if (field === 'agent' && task.agent) {
      const selectedOption = formattedAgents.find(
        option => option.value === task.agent.id
      );
      setSelectedAgent(
        selectedOption || { value: null, label: 'Sem responsável' }
      );
    }

    setEditData(prevData => ({
      ...prevData,
      [task.id]: {
        title: task.title,
        days_to_complete: task.days_to_complete || task.deadlineDays || 0,
        deadlineDays: task.deadlineDays || task.days_to_complete || 0,
        agent: { id: task.agent?.id || '' },
        isGroupTitle: task.isGroupTitle,
        status: task.status,
        description: task.description
      }
    }));
  };

  const cancelEditing = () => {
    setEditingField({ taskId: null, field: null });
    setSelectedAgent(null);
  };

  const saveChanges = taskId => {
    if (!editData[taskId]) return;

    // Encontra o agente completo baseado no ID
    const selectedAgentData = editData[taskId].agent?.id
      ? agents.find(agent => agent.id === editData[taskId].agent.id)
      : null;

    const updatedTask = {
      ...editData[taskId],
      id: taskId,
      agent: selectedAgentData
    };

    // Sincroniza os campos de prazo
    if (updatedTask.days_to_complete !== undefined) {
      updatedTask.deadlineDays = updatedTask.days_to_complete;
    }
    if (updatedTask.deadlineDays !== undefined) {
      updatedTask.days_to_complete = updatedTask.deadlineDays;
    }

    onTaskUpdate && onTaskUpdate(updatedTask);
    setEditingField({ taskId: null, field: null });
    setSelectedAgent(null);
  };

  const handleInputChange = (taskId, field, value) => {
    setEditData(prevData => ({
      ...prevData,
      [taskId]: {
        ...prevData[taskId],
        [field]: value
      }
    }));
  };

  const handleStatusChange = (taskId, currentStatus) => {
    const newStatus = !currentStatus;

    // Só atualiza se o status realmente mudou
    if (localTaskStatus[taskId] !== newStatus) {
      setLocalTaskStatus(prev => ({
        ...prev,
        [taskId]: newStatus
      }));

      const updatedTask = tasks.find(t => t.id === taskId);
      if (updatedTask && onTaskUpdate) {
        onTaskUpdate({
          ...updatedTask,
          status: newStatus
        });
      }
    }
  };

  const handleKeyDown = (e, taskId) => {
    if (e.key === 'Enter') {
      saveChanges(taskId);
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  };

  const handleDeleteTask = async () => {
    try {
      // Chama o callback do componente pai que já tem a lógica correta
      if (onTaskDelete) {
        await onTaskDelete(taskIdToDelete);
      }

      // Fecha o modal após sucesso
      setTaskIdToDelete(null);
      setShowDeleteConfirmModal(false);
    } catch (error) {
      console.error('Erro ao remover tarefa:', error);
    }
  };

  const selectAllTasks = () => {
    const allSelected = selectedTasks.length === tasks.length;
    const newSelectedTasks = allSelected ? [] : tasks.map(task => task.id);
    setSelectedTasks(newSelectedTasks);
  };

  const handleSelectTask = e => {
    const taskId = parseInt(e.target.value);
    const isChecked = e.target.checked;

    if (isChecked) {
      setSelectedTasks(prev => [...prev, taskId]);
    } else {
      setSelectedTasks(prev => prev.filter(id => id !== taskId));
    }
  };

  const handleDragEnd = result => {
    const { destination, source, draggableId } = result;

    if (!destination || destination.index === source.index) {
      return;
    }

    const taskId = parseInt(draggableId);
    const newTaskItems = Array.from(taskItems);
    const [movedTask] = newTaskItems.splice(source.index, 1);
    newTaskItems.splice(destination.index, 0, movedTask);

    // Atualiza apenas o estado local das tasks reordenadas
    setTaskItems(newTaskItems);

    // Chama o callback de reordenação sem disparar outros efeitos
    if (onTaskReorder) {
      onTaskReorder(taskId, source.index, destination.index);
    }
  };

  const convertToTask = task => {
    const updatedTask = {
      ...task,
      isGroupTitle: false,
      status: false
    };
    onTaskUpdate && onTaskUpdate(updatedTask);
  };

  const convertToTitle = task => {
    const updatedTask = {
      ...task,
      isGroupTitle: true,
      status: false
    };
    onTaskUpdate && onTaskUpdate(updatedTask);
  };

  const handleChangeAgent = (option, task) => {
    console.log('=== handleChangeAgent ===');
    console.log('Current agent ID:', task.agent?.id || null);
    console.log('New agent ID:', option.value || null);

    const currentAgentId = task.agent?.id || null;
    const newAgentId = option.value || null;

    // Verifica se o ID do agente realmente mudou
    if (currentAgentId === newAgentId) {
      console.log('No change detected, canceling...');
      // Fecha o modo de edição sem chamar onTaskUpdate
      setEditingField({ taskId: null, field: null });
      setSelectedAgent(null);
      return;
    }

    console.log('Change detected, updating...');

    const selectedAgentData = option.value
      ? agents.find(agent => agent.id === option.value)
      : null;

    const updatedTask = {
      ...task,
      agent: selectedAgentData
    };

    // Chama onTaskUpdate apenas se houver uma mudança real
    onTaskUpdate && onTaskUpdate(updatedTask);

    // Fecha o modo de edição após a atualização
    setEditingField({ taskId: null, field: null });
    setSelectedAgent(null);
  };

  const startAgentEditing = task => {
    if (editingField.taskId !== null) {
      saveChanges(editingField.taskId);
    }

    setEditingField({
      taskId: task.id,
      field: 'agent'
    });

    // Define o agente atual como selecionado
    if (task.agent) {
      const selectedOption = formattedAgents.find(
        option => option.value === task.agent.id
      );
      setSelectedAgent(
        selectedOption || { value: null, label: 'Sem responsável' }
      );
    } else {
      // Se não tem agente, seleciona "Sem responsável"
      setSelectedAgent({ value: null, label: 'Sem responsável' });
    }

    setEditData(prevData => ({
      ...prevData,
      [task.id]: {
        title: task.title,
        days_to_complete: task.days_to_complete || task.deadlineDays || 0,
        deadlineDays: task.deadlineDays || task.days_to_complete || 0,
        agent: { id: task.agent?.id || null },
        isGroupTitle: task.isGroupTitle,
        status: task.status,
        description: task.description
      }
    }));
  };

  const cancelAgentEditing = () => {
    setEditingField({ taskId: null, field: null });
    setSelectedAgent(null);
  };

  // Função para lidar com ações em massa
  const handleBulkAction = async () => {
    if (!selectedBulkAction || selectedTasks.length === 0) return;

    const { operation } = bulkBody;
    const { showSuccessNotification } = useNotification();
    let selectedAgentData = null;
    let successMessage = '';

    // Se existir um handler de bulk do pai (endpoint), use-o
    if (onBulkAction) {
      try {
        const additional = {};
        if (operation === 'SET_DEADLINE') {
          // Permitir null para "sem prazo"
          const value = bulkBody.days_to_complete;
          additional.deadlineDays =
            value === null || value === '' || value === 'null'
              ? null
              : parseInt(value) || 0;
        }
        if (operation === 'SET_AGENT') {
          additional.agentId = bulkBody.agentId ?? null;
        }
        await onBulkAction(selectedTasks, operation, additional);
        // limpar estados
        setSelectedTasks([]);
        setBulkBody({ operation: '', days_to_complete: null, agentId: null });
        setSelectedBulkAction(null);
        setSelectedAgentBulkAction(null);
        return;
      } catch (e) {
        console.error('Erro em ação em massa:', e);
        return;
      }
    }

    switch (operation) {
      case 'SET_DEADLINE':
        selectedTasks.forEach(taskId => {
          const task = tasks.find(t => t.id === taskId);
          if (task && onTaskUpdate) {
            // Permitir null para "sem prazo"
            const value = bulkBody.days_to_complete;
            const deadlineValue =
              value === null || value === '' || value === 'null'
                ? null
                : parseInt(value) || 0;
            onTaskUpdate(
              {
                ...task,
                days_to_complete: deadlineValue,
                deadlineDays: deadlineValue
              },
              true
            ); // true = supress notification
          }
        });
        successMessage = `Prazo definido para ${selectedTasks.length} tarefa(s)`;
        break;

      case 'COMPLETE':
        selectedTasks.forEach(taskId => {
          const task = tasks.find(t => t.id === taskId);
          if (task && onTaskUpdate) {
            onTaskUpdate({ ...task, status: true }, true);
          }
        });
        successMessage = `${selectedTasks.length} tarefa(s) marcada(s) como concluída(s)`;
        break;

      case 'UNCOMPLETE':
        selectedTasks.forEach(taskId => {
          const task = tasks.find(t => t.id === taskId);
          if (task && onTaskUpdate) {
            onTaskUpdate({ ...task, status: false }, true);
          }
        });
        successMessage = `${selectedTasks.length} tarefa(s) desmarcada(s)`;
        break;

      case 'DELETE':
        selectedTasks.forEach(taskId => {
          if (onTaskDelete) {
            onTaskDelete(taskId, true); // true = supress notification
          }
        });
        successMessage = `${selectedTasks.length} tarefa(s) excluída(s)`;
        break;

      case 'SET_AGENT':
        selectedAgentData = bulkBody.agentId
          ? agents.find(agent => agent.id === bulkBody.agentId)
          : null;

        selectedTasks.forEach(taskId => {
          const task = tasks.find(t => t.id === taskId);
          if (task && onTaskUpdate) {
            const updatedTask = {
              ...task,
              agent: selectedAgentData
                ? {
                    id: selectedAgentData.id,
                    firstName: selectedAgentData.firstName || '',
                    lastName: selectedAgentData.lastName || '',
                    name: `${selectedAgentData.firstName || ''} ${
                      selectedAgentData.lastName || ''
                    }`.trim()
                  }
                : { id: null }
            };

            onTaskUpdate(updatedTask, true);
          }
        });

        if (selectedAgentData) {
          successMessage = `Responsável "${selectedAgentData.firstName} ${selectedAgentData.lastName}" definido para ${selectedTasks.length} tarefa(s)`;
        } else {
          successMessage = `Responsável removido de ${selectedTasks.length} tarefa(s)`;
        }
        break;

      default:
        successMessage = `Ação aplicada a ${selectedTasks.length} tarefa(s)`;
        break;
    }

    if (successMessage) {
      showSuccessNotification(successMessage);
    }

    // Limpa os estados após aplicar a ação
    setSelectedTasks([]);
    setBulkBody({
      operation: '',
      days_to_complete: null,
      agentId: null
    });
    setSelectedBulkAction(null);
    setSelectedAgentBulkAction(null);
  };

  const isBulkActionDisabled =
    selectedTasks.length === 0 ||
    !selectedBulkAction?.value ||
    isDisabled ||
    (selectedBulkAction?.value === 'SET_DEADLINE' &&
      (bulkBody.days_to_complete === undefined ||
        bulkBody.days_to_complete < 0)) ||
    (selectedBulkAction?.value === 'SET_AGENT' &&
      bulkBody.agentId === undefined);

  const fetchAgentPhotos = async () => {
    setIsLoadingPhotos(true);
    try {
      // Extrai IDs únicos de agentes a partir das tasks
      const agentIds = [
        ...new Set(tasks.map(task => task.agent?.id).filter(id => id))
      ];
      if (agentIds.length === 0) {
        setIsLoadingPhotos(false);
        return;
      }

      // Faz requisições paralelas para buscar fotos
      const requests = agentIds.map(id => AgentPhotoService.find(id));
      const responses = await Promise.all(requests);
      const photos = {};

      // Processa as respostas
      for (let i = 0; i < responses.length; i++) {
        if (responses[i].ok) {
          const { url } = await responses[i].json();
          photos[agentIds[i]] = url;
        }
      }

      setAgentPhotos(photos);
    } catch (error) {
      console.error('Erro ao buscar fotos dos agentes:', error);
    } finally {
      setIsLoadingPhotos(false);
    }
  };

  const getAgentPhoto = agentId => {
    return agentId && agentPhotos[agentId] ? agentPhotos[agentId] : null;
  };

  const getAgents = async () => {
    const api = useApi();
    const response = await api.agents.listSummaryData();
    setAgents(response);

    const optionsAgents = response.map(item => ({
      value: item.id,
      label: `${item.firstName} ${item.lastName}`
    }));

    optionsAgents.unshift({ value: null, label: 'Sem responsável' });

    setFormattedAgents(optionsAgents);
  };

  return (
    <>
      {/* Modais */}
      <TemplateTaskEditModal
        show={showEditModal}
        handleClose={() => setShowEditModal(false)}
        data={taskToEdit}
        agents={agents}
        onTaskUpdate={onTaskUpdate}
      />
      <TemplateDaysToCompleteEditModal
        show={showEditDaysToCompleteModal}
        close={() => setShowEditDaysToCompleteModal(false)}
        taskData={taskToDaysToCompleteEdit}
        setTaskData={data => {
          setTaskToDaysToCompleteEdit(data);
          if (data.id && onTaskUpdate) {
            const task = tasks.find(t => t.id === data.id);
            if (task) {
              onTaskUpdate({
                ...task,
                days_to_complete: data.days_to_complete,
                deadlineDays: data.days_to_complete
              });
            }
          }
        }}
      />
      <DialogModal
        showModal={showDeleteConfirmModal}
        handleClose={() => setShowDeleteConfirmModal(false)}
        title="Confirmar exclusão"
        message="Tem certeza que deseja excluir esta tarefa?"
        response={handleDeleteTask}
      />

      {/* Bulk Actions */}
      {showBulkActions && (
        <Flex
          justifyContent={'between'}
          alignItems={'center'}
          className="px-3 mb-2 bulk-actions"
        >
          <Flex>
            <Button
              variant="link"
              size="sm"
              role="none"
              style={{ paddingRight: '10px' }}
            >
              <span className="d-none">draggable</span>
            </Button>
            <Form.Check
              type="checkbox"
              disabled={isDisabled}
              checked={
                selectedTasks.length === taskItems.length &&
                taskItems.length > 0
              }
              className="me-3"
              onClick={() => selectAllTasks()}
              ref={selectAllCheckboxRef}
              label={`Selecionar todas (${
                tasks.filter(task => !task.isGroupTitle).length
              })`}
            />
          </Flex>

          {selectedTasks.length > 0 && (
            <Flex alignItems="center" className="gap-2">
              <SingleSelect
                options={optionsBulkActions}
                placeholder="Ações em massa"
                size="sm"
                isDisabled={isDisabled}
                value={selectedBulkAction}
                onChange={option => {
                  setSelectedBulkAction(option);
                  setBulkBody(prev => ({ ...prev, operation: option.value }));
                }}
              />
              {selectedBulkAction?.value === 'SET_DEADLINE' && (
                <Form.Select
                  size="sm"
                  style={{ width: '150px' }}
                  value={
                    bulkBody.days_to_complete === null
                      ? 'null'
                      : bulkBody.days_to_complete
                  }
                  onChange={e => {
                    const value = e.target.value;
                    setBulkBody({
                      ...bulkBody,
                      days_to_complete:
                        value === 'null' ? null : parseInt(value) || 0
                    });
                  }}
                >
                  <option value="null">Sem prazo</option>
                  <option value={0}>0 (Data de criação)</option>
                  {[...Array(31).keys()].slice(1).map(i => (
                    <option key={i} value={i}>
                      {i} dia{i > 1 ? 's' : ''}
                    </option>
                  ))}
                </Form.Select>
              )}
              {selectedBulkAction?.value === 'SET_AGENT' && (
                <SingleSelect
                  options={formattedAgents}
                  placeholder="Responsável"
                  size="sm"
                  isDisabled={isDisabled}
                  value={selectedAgentBulkAction}
                  onChange={option => {
                    setSelectedAgentBulkAction(option);
                    setBulkBody({ ...bulkBody, agentId: option.value });
                  }}
                />
              )}
              <Button
                size="sm"
                disabled={isBulkActionDisabled}
                onClick={handleBulkAction}
              >
                Aplicar
              </Button>
            </Flex>
          )}
        </Flex>
      )}

      {/* Lista de Tarefas */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="tasks" type="TASK">
          {provided => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {taskItems.map((task, index) => (
                <Draggable
                  key={task.id}
                  draggableId={task.id.toString()}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className={snapshot.isDragging ? '' : ''}
                    >
                      <Card
                        ref={
                          editingField.taskId === task.id
                            ? editingCardRef
                            : null
                        }
                        className={`task border-0 shadow-sm mb-2 ${
                          task.isGroupTitle ? '' : ''
                        }`}
                        style={{
                          borderRadius: '8px',
                          opacity: snapshot.isDragging ? 0.8 : 1
                        }}
                      >
                        <Card.Body
                          as={Flex}
                          justifyContent="between"
                          alignItems="center"
                          className="py-2 px-3"
                          style={{ minHeight: '64px' }}
                        >
                          <Flex alignItems="center" className="flex-1">
                            <div
                              className="p-0 text-400 me-3"
                              title="Arraste para reorganizar"
                              style={{
                                cursor: task.status ? 'not-allowed' : 'grab',
                                color: '#6c757d'
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faGripVertical}
                                size="sm"
                              />
                            </div>
                            {showBulkActions && !task.isGroupTitle && (
                              <Form.Check
                                type="checkbox"
                                value={task.id}
                                checked={selectedTasks.includes(task.id)}
                                onChange={handleSelectTask}
                                className="me-2"
                                disabled={isDisabled}
                              />
                            )}

                            {!task.isGroupTitle && (
                              <Button
                                variant="link"
                                size="sm"
                                className="ms-2"
                                style={{
                                  width: '24px',
                                  height: '24px',
                                  padding: '0',
                                  color: localTaskStatus[task.id]
                                    ? 'white'
                                    : '#e0e0e0',
                                  backgroundColor: localTaskStatus[task.id]
                                    ? '#00D27A'
                                    : '',
                                  border: `1px solid ${
                                    localTaskStatus[task.id]
                                      ? '#00D27A'
                                      : '#e0e0e0'
                                  }`,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                                onClick={() =>
                                  handleStatusChange(
                                    task.id,
                                    localTaskStatus[task.id]
                                  )
                                }
                                disabled={isDisabled}
                              >
                                <FontAwesomeIcon
                                  icon={faCheck}
                                  className="fs-11"
                                />
                              </Button>
                            )}

                            {/* Título da tarefa */}
                            {editingField.taskId === task.id &&
                            editingField.field === 'title' ? (
                              <Form.Control
                                ref={titleInputRef}
                                value={editData[task.id]?.title || ''}
                                onChange={e =>
                                  handleInputChange(
                                    task.id,
                                    'title',
                                    e.target.value
                                  )
                                }
                                onKeyDown={e => handleKeyDown(e, task.id)}
                                onBlur={() => saveChanges(task.id)}
                                size="sm"
                                className="border-0 shadow-none flex-1"
                                disabled={task.status}
                              />
                            ) : (
                              <Button
                                variant="link"
                                onClick={() =>
                                  !isDisabled && startEditing(task, 'title')
                                }
                                disabled={task.status}
                                className={`task-title ${
                                  task.isGroupTitle
                                    ? 'fw-semibold fs-8 text-body-emphasis'
                                    : ''
                                } ${!isDisabled ? 'cursor-pointer' : ''}`}
                                style={{
                                  textDecoration: localTaskStatus[task.id]
                                    ? 'line-through'
                                    : 'none',
                                  color: localTaskStatus[task.id]
                                    ? '#6c757d'
                                    : 'inherit',
                                  cursor: !isDisabled ? 'pointer' : 'default'
                                }}
                              >
                                {task.title}
                              </Button>
                            )}
                          </Flex>

                          {!task.isGroupTitle && (
                            <Flex alignItems="center" className="task-actions">
                              {/* Dias para Conclusão */}
                              <Button
                                variant="link"
                                className={`mx-3 p-0 fs-10 text-decoration-none ${getDaysToCompleteColor(
                                  task.days_to_complete
                                )}`}
                                onClick={() => openDaysToCompleteModal(task)}
                                disabled={isDisabled}
                              >
                                {task.days_to_complete === null ||
                                task.days_to_complete === undefined ? (
                                  <>
                                    <span className="me-1">Sem prazo</span>{' '}
                                    <FontAwesomeIcon icon={faCalendar} />
                                  </>
                                ) : task.days_to_complete > 0 ? (
                                  `${task.days_to_complete} dias`
                                ) : task.days_to_complete === 0 ? (
                                  <>
                                    <span className="me-1">
                                      Data de criação do ticket
                                    </span>{' '}
                                    <FontAwesomeIcon icon={faCalendar} />
                                  </>
                                ) : (
                                  <>
                                    <span className="me-1">Sem prazo</span>{' '}
                                    <FontAwesomeIcon icon={faCalendar} />
                                  </>
                                )}
                              </Button>

                              {/* Tooltip para descrição */}
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip
                                    id={`tooltip-description-${task.id}`}
                                  >
                                    {task.description || 'Descrição da tarefa'}
                                  </Tooltip>
                                }
                              >
                                <Button
                                  variant="link"
                                  className="mx-3 p-0 text-secondary"
                                  disabled={isDisabled}
                                  onClick={() => openEditModal(task)}
                                >
                                  <FontAwesomeIcon icon={faAlignJustify} />
                                </Button>
                              </OverlayTrigger>

                              {/* Avatar/seleção de agente */}
                              {editingField.taskId === task.id &&
                              editingField.field === 'agent' ? (
                                <SingleSelect
                                  ref={agentSelectRef}
                                  options={formattedAgents}
                                  value={selectedAgent}
                                  onChange={option =>
                                    handleChangeAgent(option, task)
                                  }
                                  onBlur={() => cancelAgentEditing()}
                                  className="mx-3"
                                  style={{ width: '150px' }}
                                  isDisabled={isDisabled}
                                />
                              ) : task.agent?.id ? (
                                <div
                                  onClick={() =>
                                    !isDisabled && startAgentEditing(task)
                                  }
                                  role="button"
                                  className="mx-3"
                                  style={{
                                    cursor: !isDisabled ? 'pointer' : 'default'
                                  }}
                                >
                                  <AvatarTask
                                    src={getAgentPhoto(task.agent.id)}
                                    name={`${task.agent.firstName} ${task.agent.lastName}`}
                                  />
                                </div>
                              ) : (
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  className="mx-3 rounded-circle"
                                  style={{
                                    width: '30px',
                                    height: '30px',
                                    padding: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                  onClick={() => startAgentEditing(task)}
                                  disabled={isDisabled}
                                >
                                  <FontAwesomeIcon
                                    icon={faUserPlus}
                                    className="fs-11"
                                  />
                                </Button>
                              )}

                              {/* Dropdown de ações para tarefas */}
                              <Dropdown
                                className="btn-reveal-trigger opacity-100"
                                align="end"
                              >
                                <Dropdown.Toggle
                                  as={Button}
                                  variant="link"
                                  className="p-0 text-secondary ms-2"
                                  disabled={isDisabled}
                                >
                                  <FontAwesomeIcon icon={faEllipsisH} />
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                  <Dropdown.Item
                                    onClick={() => openEditModal(task)}
                                  >
                                    Editar
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() => convertToTitle(task)}
                                  >
                                    Converter para título
                                  </Dropdown.Item>
                                  <Dropdown.Divider />
                                  <Dropdown.Item
                                    className="text-danger"
                                    onClick={() => {
                                      setTaskIdToDelete(task.id);
                                      setShowDeleteConfirmModal(true);
                                    }}
                                  >
                                    Excluir
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </Flex>
                          )}

                          {/* Ações para títulos de grupo */}
                          {task.isGroupTitle && (
                            <Dropdown
                              className="btn-reveal-trigger opacity-100"
                              align="end"
                            >
                              <Dropdown.Toggle
                                as={Button}
                                variant="link"
                                className="p-0 text-secondary ms-2"
                                disabled={isDisabled}
                              >
                                <FontAwesomeIcon icon={faEllipsisH} />
                              </Dropdown.Toggle>

                              <Dropdown.Menu>
                                <Dropdown.Item
                                  onClick={() => openEditModal(task)}
                                >
                                  Editar título
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() => convertToTask(task)}
                                >
                                  Converter para tarefa
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Item
                                  className="text-danger"
                                  onClick={() => {
                                    setTaskIdToDelete(task.id);
                                    setShowDeleteConfirmModal(true);
                                  }}
                                >
                                  Excluir
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          )}
                        </Card.Body>
                      </Card>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </>
  );
};

export default SimpleTaskList;
